import React, { useEffect, useState } from "react";
import { Download, Link2, Play, Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Lightbox, mediaSrc } from "./MessageExtras";
import { api, mediaUrl } from "../lib/api";

const KIND_LABEL = { image: "panel", video: "adegan", slide: "slide" };

// «Bagikan» → one public link (/s/<code>, 30 days) copied to the clipboard; «Simpan ke Galeri» flags every finished image/clip as a Gallery item.
function StripActions({ plan, cid }) {
  const [busy, setBusy] = useState(null);
  const [saved, setSaved] = useState(!!plan.gallery_saved_at);
  const [link, setLink] = useState(null);
  useEffect(() => { setSaved(!!plan.gallery_saved_at); }, [plan.gallery_saved_at]);
  const share = async () => {
    setBusy("share");
    try {
      const r = await api.post(`/conversations/${cid}/content-plans/${plan.id}/share`);
      const url = `${window.location.origin}${r.data.path}`;
      setLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Tautan publik disalin — berlaku 30 hari"); } catch { toast.success("Tautan publik siap"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat tautan"); } finally { setBusy(null); }
  };
  const save = async () => {
    setBusy("save");
    try { const r = await api.post(`/conversations/${cid}/content-plans/${plan.id}/save-gallery`); setSaved(true); toast.success(r.data.saved ? `${r.data.saved} bagian tersimpan di Galeri` : "Presentasinya sudah ada di Galeri (Dokumen)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(null); }
  };
  const btn = "flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50";
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <a href={mediaUrl(`/conversations/${cid}/content-plans/${plan.id}/results.zip`)} download data-testid="plan-results-download-all" className={btn}><Download size={12} /> Unduh semua</a>
      <button type="button" onClick={share} disabled={busy === "share"} data-testid="plan-results-share" className={btn}>{busy === "share" ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Bagikan</button>
      <button type="button" onClick={save} disabled={busy === "save" || saved} data-testid="plan-results-save-gallery" className={`${btn} ${saved ? "text-emerald-700" : ""}`}>{saved ? <BookmarkCheck size={12} /> : <Bookmark size={12} />} {saved ? "Di Galeri" : "Simpan ke Galeri"}</button>
      {link && <a href={link} target="_blank" rel="noopener noreferrer" className="max-w-[220px] truncate text-[11px] text-[#2F6BFF] underline" data-testid="plan-results-share-link" data-no-i18n>{link}</a>}
    </div>
  );
}

// Content Plan card: the finished parts (panels, clips, slides) in story order — the whole piece at a glance, whatever was composed.
export function PlanResultsStrip({ plan, cid }) {
  const [open, setOpen] = useState(null);
  const secs = (plan?.sections || []).filter((s) => s.result && (s.result.path || s.result.url || s.result.drive_id));
  if (!secs.length) return null;
  const kind = KIND_LABEL[secs[0].result.type] || "bagian";
  return (
    <div className="mt-2" data-testid="plan-results-strip">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{secs.length}/{(plan.sections || []).length} {kind} jadi · klik untuk memperbesar</p>
        {cid && plan.id && <StripActions plan={plan} cid={cid} />}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {secs.map((s) => <ResultThumb key={s.n} s={s} onOpen={() => setOpen(s)} />)}
      </div>
      {secs.some((s) => (s.result_history || []).length > 0) && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid="plan-result-history">
          {secs.filter((s) => (s.result_history || []).length > 0).map((s) => (s.result_history || []).map((h, i) => (
            <button key={`${s.n}-${i}`} type="button" onClick={() => setOpen({ ...s, result: h, title: `${s.title || ""} · versi lama ${i + 1}` })} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-500 hover:border-[#2F6BFF]/40 hover:text-[#2F6BFF]" data-testid={`plan-result-prev-${s.n}-${i + 1}`}>#{s.n} versi lama {i + 1}</button>
          )))}
        </div>
      )}
      {open && open.result.type !== "video" && <Lightbox media={{ path: open.result.path, url: open.result.url, name: `#${open.n} ${open.title || ""}` }} onClose={() => setOpen(null)} mark={cid ? { cid, part: open.n } : null} />}
      {open && open.result.type === "video" && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 p-4" onClick={() => setOpen(null)} data-testid="plan-result-video-viewer">
          <video src={mediaSrc(open.result)} controls autoPlay className="max-h-[85vh] max-w-full rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function ResultThumb({ s, onOpen }) {
  const r = s.result;
  const square = r.type === "image";
  const box = square ? "h-[96px] w-[96px]" : "h-[72px] w-[128px]";
  return (
    <button type="button" onClick={onOpen} title={s.title} data-testid={`plan-result-${s.n}`} className="group shrink-0 text-left">
      <div className={`relative overflow-hidden rounded-lg border border-slate-200 bg-slate-900 ${box}`}>
        {r.type === "video"
          ? <video src={mediaSrc(r)} muted preload="metadata" className="h-full w-full object-cover" />
          : <img src={mediaSrc(r)} alt={s.title || `#${s.n}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-150 group-hover:scale-[1.04]" />}
        {r.type === "video" && <span className="absolute inset-0 flex items-center justify-center text-white/90"><Play size={20} fill="currentColor" /></span>}
        <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] font-bold text-white">#{s.n}</span>
        {s.revised && <span className="absolute bottom-1 right-1 rounded bg-emerald-600/90 px-1 text-[9px] font-bold text-white" title={`${(s.result_history || []).length} versi sebelumnya tersimpan`} data-testid={`plan-result-revised-${s.n}`}>diperbaiki</span>}
      </div>
      <p className={`mt-0.5 truncate text-[11px] text-slate-500 ${square ? "w-[96px]" : "w-[128px]"}`} data-no-i18n>{s.title}</p>
    </button>
  );
}
